import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button, Row, Col } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import { omit as _omit } from 'lodash';

import { formStatusDispatchHelper, formReset } from 'actions/FormStatusActions';
import { addNotification } from 'actions/NotificationsActions';
import { pipelineOptionFromEntity } from 'components/forms/autocomplete/Pipeline';
import { clientOptionFromEntity } from 'components/forms/autocomplete/Client';
import { getLeadPriorities, getChannelTypes } from 'helpers/leads';

import EntityStateUpdater from 'component/EntityStateUpdater';

import Loader from 'components/layout/Loader';
import Data from 'components/Data';
import Entity from 'components/Entity';

import ClientForm from 'components/forms/clients/ClientForm';

import FormInput from 'components/forms/FormInput';
import FormAutocomplete from 'components/forms/FormAutocomplete';
import FormRichEditor from 'components/forms/FormRichEditor';
import FormErrors from 'components/forms/FormErrors';

import { entityMetaMapping } from 'js/constants';

class LeadForm extends EntityStateUpdater {
  constructor(props) {
    super(props);

    this.state = {
      entityLoaded: false,
      entityChanged: false,
      createClient: false,
    };

    this.entity = new Entity();

    // if we get lead data from the outside, we can pass it on to our data entity
    this.entity.setData(props.lead || { priority: 1 });

    if (props.meta[entityMetaMapping.lead] !== undefined && !props.meta[entityMetaMapping.lead].pending) {
      console.log('LeadForm constructor, has Meta', entityMetaMapping.lead);
      this.entity.setMeta(props.meta, entityMetaMapping.lead);

      this.state = {
        ...this.state,
        entityLoaded: true,
        entity: this.entity,
      };
    }
  }

  componentDidUpdate = prevProps => {
    const {
      intl: { messages },
      meta,
      newClientFormStatus,
      doNotify,
    } = this.props;

    const leadMeta = meta[entityMetaMapping.lead];
    const isMetaBecomeLoaded = leadMeta && !leadMeta.pending && !this.entity.getMetaLoaded();

    if (isMetaBecomeLoaded) {
      this.entity.setMeta(meta, entityMetaMapping.lead);
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }

    // if the user created new client, close the client edit form and select the client
    if (prevProps.newClientFormStatus.pending && !newClientFormStatus.pending && !newClientFormStatus.hasError) {
      doNotify(1, messages.client_data_saved);
      this.updateEntity('target', newClientFormStatus.response, [], () => this.setState({ createClient: false }));
    }
  };

  formSubmit = e => {
    e.preventDefault();

    this.setState(
      prevState => ({ entity: prevState.entity.validate() }),
      () => {
        if (this.state.entity.isValid) {
          this.props.onSubmit(_omit(this.state.entity.processedData, ['handover']));
        }
      }
    );
  };

  updatePipeline = (formInputName, pipeline) => {
    if (pipeline) {
      this.updateEntityMultiple([formInputName, 'currentStage'], [pipeline, pipeline.stages[0].id], [[], []]);
    } else {
      this.updateEntityMultiple([formInputName, 'currentStage'], [null, null]);
    }
  };

  onNewClientSubmit = args => {
    this.props.onNewClientSubmit(args);
  };

  render() {
    const {
      intl: { messages },
      formStatus,
      newClientFormStatus,
      pipelineType,
    } = this.props;
    const { entity, entityChanged, entityLoaded } = this.state;

    return (
      <Row>
        <Data dataName="lead" /> {/* fetch meta data */}
        <Col xs={12}>
          <FormErrors formStatus={formStatus} />

          {entityLoaded ? (
            <div>
              {this.state.createClient ? (
                <div>
                  <ClientForm type="new" onSubmit={this.onNewClientSubmit} formStatus={newClientFormStatus} />
                </div>
              ) : (
                <form method="post" onSubmit={this.formSubmit} id="leadMainForm">
                  <div className="panel panel-transparent">
                    <div className="form-group-attached">
                      <FormAutocomplete
                        label={messages.pipeline}
                        name="pipeline"
                        value={pipelineOptionFromEntity(entity.getPipeline())}
                        meta={entity.getPipelineMetaInformation()}
                        validation={entity.getPipelineValidationResult()}
                        onChange={this.updatePipeline}
                        returnValueObject
                        autocompleteType="pipeline"
                        disabled={formStatus.pending}
                        urlFilters={{ filters: { type: pipelineType } }}
                      />
                      <FormInput
                        name="title"
                        label={messages.title}
                        value={entity.getTitle()}
                        meta={entity.getTitleMetaInformation()}
                        validation={entity.getTitleValidationResult()}
                        onChange={this.updateEntity}
                        disabled={formStatus.pending}
                      />
                      <FormRichEditor
                        id="lead-new"
                        name="description"
                        label={messages.description}
                        value={entity.getDescription()}
                        meta={entity.getDescriptionMetaInformation()}
                        validation={entity.getDescriptionValidationResult()}
                        onChange={this.updateEntity}
                        disabled={formStatus.pending}
                      />
                      <FormAutocomplete
                        label={messages.assignee}
                        name="assignee"
                        value={clientOptionFromEntity(entity.getAssignee())}
                        meta={entity.getAssigneeMetaInformation()}
                        validation={entity.getAssigneeValidationResult()}
                        onChange={this.updateEntity}
                        returnValueObject
                        autocompleteType="user"
                        disabled={formStatus.pending}
                      />

                      <Row className="clearfix">
                        <Col sm={8}>
                          <FormAutocomplete
                            name="target"
                            label={messages.client}
                            value={clientOptionFromEntity(entity.getTarget())}
                            meta={entity.getTargetMetaInformation()}
                            validation={entity.getTargetValidationResult()}
                            autocompleteType="client"
                            onChange={this.updateEntity}
                            disabled={formStatus.pending}
                            returnValueObject
                          />
                        </Col>
                        <Col sm={4}>
                          <div className="form-group form-group-default">
                            <label>{messages.client_not_found}</label>
                            <div className="text-right">
                              <Button
                                block
                                onClick={() => this.setState({ createClient: true })}
                                disabled={formStatus.pending}
                              >
                                <i className="fa fa-plus m-r-10" /> {messages.create_client}
                              </Button>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <div className="form-group-attached m-t-15">
                      <FormAutocomplete
                        label={messages.priority}
                        name="priority"
                        value={entity.getPriority()}
                        meta={entity.getPriorityMetaInformation()}
                        validation={entity.getPriorityValidationResult()}
                        onChange={this.updateEntity}
                        disabled={formStatus.pending}
                        options={getLeadPriorities(messages).map(leadPriority => {
                          return {
                            value: leadPriority.value,
                            label: (
                              <span>
                                {leadPriority.icon} {leadPriority.label}
                              </span>
                            ),
                          };
                        })}
                      />
                      <FormInput
                        name="source"
                        label={messages.source}
                        value={entity.getSource()}
                        meta={entity.getSourceMetaInformation()}
                        validation={entity.getSourceValidationResult()}
                        onChange={this.updateEntity}
                        disabled={formStatus.pending}
                      />
                      <FormAutocomplete
                        label={messages.lead_channel}
                        name="channel"
                        value={entity.getChannel()}
                        meta={entity.getChannelMetaInformation()}
                        validation={entity.getChannelValidationResult()}
                        onChange={this.updateEntity}
                        disabled={formStatus.pending}
                        options={getChannelTypes(messages)}
                      />
                    </div>

                    <div className="text-right m-t-15">
                      <Button
                        bsSize="lg"
                        disabled={formStatus.pending}
                        type="submit"
                        bsStyle={entityChanged ? 'primary' : 'default'}
                      >
                        <i
                          className={'fa-fw ' + (formStatus.pending ? ' fa fa-circle-notch fa-spin' : 'fal fa-save')}
                        />{' '}
                        {messages.save}
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <Loader />
          )}
        </Col>
      </Row>
    );
  }
}
LeadForm.defaultProps = {
  pipelineType: 'lead',
};

const mapStateToProps = state => {
  return {
    meta: state.entityMeta,
    newClientFormStatus: state.formStatus.clientNew,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    onNewClientSubmit: args => {
      formStatusDispatchHelper('clientNew', '/api/clients.json', args, 'post', dispatch);
    },
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
  };
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(LeadForm)
  )
);
